import { AdjustmentsHorizontalIcon, UserPlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import copyIcon from "asset/img/icons/copyIcon.svg";
import copyIconHover from "asset/img/icons/copyIconHover.svg";
import Text from "component/Text/Text";
import { IButtonIcon } from "interface/component";
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import React, { useEffect, useState } from "react";

const BUTTON_TYPE = Object.freeze({
  PASTE: {
    icon: copyIcon,
    iconHover: copyIconHover,
    size: " w-full h-full",
    label: "",
  },
  ADD_USER: {
    icon: (
      <UserPlusIcon className="h-6 w-6 text-light-grey-700 hover:text-light-grey-800 dark:text-dark-neutral-gray-700 hover:dark:text-dark-neutral-gray-900" />
    ),
    // iconHover: copyIconHover,
    size: " w-full h-full",
    label: "",
  },
  FILTER: {
    icon: (
      <AdjustmentsHorizontalIcon className="h-6 w-6 text-light-grey-700 hover:text-light-grey-800 dark:text-dark-neutral-gray-700 hover:dark:text-dark-neutral-gray-900" />
    ),
    size: " w-full h-full",
    label: "",
  },
});

const ButtonIcon: React.FC<IButtonIcon> = (props: IButtonIcon) => {
  const {
    type,
    disabled,
    toggle,
    sizeFull,
    iconClassName,
    className,
    blinkStyles,
    isCustomIcon,
    showTooltip,
    tooltipText,
    tooltipPosition,
    ...primeReactProps
  } = props;
  const [iconHover, setIconHover] = useState(false);
  const [blinkStyle, setBlinkStyle] = useState("");

  useEffect(() => {
    let intervalId: any = null;
    let shouldSetBlinkStyle = true;

    if (blinkStyles) {
      intervalId = setInterval(() => {
        setIconHover((prevIconHover) => !prevIconHover);

        if (shouldSetBlinkStyle && blinkStyles) {
          setBlinkStyle(blinkStyles);
        } else {
          setBlinkStyle("");
        }

        // Invert the flag for the next iteration
        shouldSetBlinkStyle = !shouldSetBlinkStyle;
      }, 500);

      setTimeout(() => {
        if (intervalId) {
          clearInterval(intervalId);
        }
        setIconHover(false);
        setBlinkStyle("");
      }, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [blinkStyles]);

  const getIcon: any = (buttonType: any) => {
    return BUTTON_TYPE[buttonType as keyof typeof BUTTON_TYPE];
  };

  const icon: any = getIcon(type);

  const getBrightness: any = () => {
    return toggle ? "" : ` group-focus:brightness-75 ${iconClassName} w-3.5 h-3.5`;
  };

  const getIconImage = () => {
    if (iconHover) {
      return icon.iconHover;
    } else if (disabled) {
      return icon.iconDisabled;
    } else {
      return icon.icon;
    }
  };

  const brightnessStyle = disabled ? getBrightness() : "";
  const sizeStyle = !sizeFull ? ` ${iconClassName} h-3.5 w-3.5` : "";
  const imageStyle = `${brightnessStyle} ${sizeStyle}`;

  return (
    <div
      className={`${icon.size} flex items-center justify-center ${blinkStyle}`}
      id={"button-icon-tooltip"}
    >
      <Button
        className={`${className ?? ""} ${!disabled ? "cursor-pointer" : "rounded-full"}`}
        disabled={disabled}
        {...primeReactProps}
        onMouseEnter={() => !isCustomIcon && setIconHover(!disabled)}
        onMouseLeave={() => !isCustomIcon && setIconHover(false)}
        onFocus={() => !isCustomIcon && setIconHover(!disabled)}
        onBlur={() => !isCustomIcon && setIconHover(false)}
      >
        {isCustomIcon ? getIconImage() : <img src={getIconImage()} className={imageStyle} alt="" />}

        {icon.label && (
          <Text
            label={icon.label}
            className="text-secondary-blueAzure-500 font-BrownMedium hover:text-primary-pText-900 ml-3 cursor-pointer text-body-copy-1"
          />
        )}
      </Button>

      {showTooltip && (
        <Tooltip
          className={`dark-tooltip rounded-toastBorderRadius bg-dark-neutral-gray-800 p-3 text-sm !text-dark-neutral-gray-100`}
          target={`#button-icon-tooltip`}
          position={tooltipPosition ?? "top"}
          showDelay={70}
          hideDelay={120}
        >
          {tooltipText}
        </Tooltip>
      )}
    </div>
  );
};

export default ButtonIcon;
